import { useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { Progress } from '@backstage/core-components';
import { useTechDocsReaderPage } from '@backstage/plugin-techdocs-react';
import { DocTreeNodeRow } from './DocTreeNodeRow';
import { findActivePath } from './findActivePath';
import { useEntityDocTree } from './useEntityDocTree';

/*
 * A `PrimarySidebar` addon: the whole catalog's doc tree, placed below
 * whichever doc set's own page list the reader is showing. Every reader page
 * gets this rail, so "all documentation in one place" holds from wherever a
 * reader was opened, not only from the Search page's Tree tab.
 *
 * Styled to match the doc-nav rail it sits below (readerStyles.ts's
 * `.md-nav__title`/`.md-nav__link`), and — despite rendering inside the
 * mkdocs shadow root via the addon's own portal — regular `makeStyles`
 * classes apply here the same as they do in `OnThisPageAddon`.
 */
const useStyles = makeStyles({
  root: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: '1px solid var(--hone-portal-hairline-soft)',
  },
  title: {
    display: 'block',
    margin: 0,
    padding: '0 12px 10px',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--bui-fg-primary)',
  },
});

export const DocsTreeSidebarAddon = () => {
  const classes = useStyles();
  const { value, loading } = useEntityDocTree();
  const { entityRef } = useTechDocsReaderPage();
  const activeRef = stringifyEntityRef(entityRef);
  const forceExpandKeys = useMemo(
    () => findActivePath(value ?? [], activeRef),
    [value, activeRef],
  );

  if (loading) return <Progress />;
  if (!value || value.length === 0) return null;

  return (
    <nav className={classes.root} aria-label="All documentation">
      <span className={classes.title}>All documentation</span>
      {value.map(node => (
        <DocTreeNodeRow
          key={node.key}
          node={node}
          depth={0}
          autoExpand={false}
          forceExpandKeys={forceExpandKeys}
          activeRef={activeRef}
        />
      ))}
    </nav>
  );
};
